


















import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import MmHintedButton from '../../components/MmHintedButton.vue';
import MmScrollablePopup from '../../components/MmScrollablePopup.vue';
import { fetchAssignments } from '../../api/assignments';
import { buildPageQuery, createPaginationState } from '../../api/pagination';
import { createCancelSource } from '../../api/mmHttp';
import { isCanceled } from '../../core/errors';
import { orDash } from '../../core/format';
import { MmNoAuditorErrorBody } from '../../types/domain';

const KEY_PREFIX = 'priceMonitoring.mobileMonitoring.';

const STORE_LINE_SEPARATOR = ' · ';

const STORE_LOOKUP_PAGE_CAP = 500;

@Component({
	components: { MmHintedButton, MmScrollablePopup },
})
export default class NoAuditorPopup extends Vue {
	@Prop({ required: true }) readonly visible!: boolean;

	@Prop({ default: null }) readonly body!: MmNoAuditorErrorBody | null;

	@Prop({ default: null }) readonly tradingId!: number | null;

	storeNames: { [code: string]: string } = {};

	private namesSource: any = null;

	get hasBody(): boolean {
		return this.body !== null;
	}

	get codes(): string[] {
		const source = this.body !== null ? this.body.competitorStoreCodes : null;

		if (!source || !Array.isArray(source)) {
			return [];
		}

		return source.map((code) => String(code));
	}

	get displayLines(): string[] {
		const codes = this.codes;
		const lines: string[] = [];

		for (let i = 0; i < codes.length; i += 1) {
			lines.push(this.formatStoreLine(codes[i]));
		}

		return lines;
	}

	get displayCount(): number {
		return this.codes.length;
	}

	get hasNoAuditorsAtAll(): boolean {
		return this.codes.length === 0;
	}

	get titleText(): string {
		return this.$t(KEY_PREFIX + 'common.noAuditorTitle') as string;
	}

	get subtitleText(): string {
		if (!this.hasBody) {
			return '';
		}

		if (this.hasNoAuditorsAtAll) {
			return this.$t(KEY_PREFIX + 'taskForm.noAuditorNone') as string;
		}

		const base = this.$t(KEY_PREFIX + 'common.noAuditorSubtitle') as string;

		if (this.displayCount > 0) {
			const countText = this.$t(KEY_PREFIX + 'taskForm.noAuditorCount', {
				count: this.displayCount,
			}) as string;

			return base + ' ' + countText;
		}

		return base;
	}

	get goToAssignmentsText(): string {
		return this.$t(KEY_PREFIX + 'taskForm.goToAssignments') as string;
	}

	get canGoToAssignments(): boolean {
		return this.hasBody && !!(this.$listeners && this.$listeners['go-to-assignments']);
	}

	handleClose(): void {
		this.$emit('close');
	}

	handleGoToAssignments(): void {
		this.$emit('go-to-assignments');
	}

	@Watch('visible')
	onVisibleChanged(next: boolean): void {
		if (next === true) {
			this.loadStoreNames();

			return;
		}

		this.cancelNamesRequest();
	}

	beforeDestroy(): void {
		this.cancelNamesRequest();
	}

	private formatStoreLine(code: string): string {
		const mapped = this.storeNames[code];
		const name = mapped !== undefined && String(mapped).trim() !== ''
			? String(mapped).trim()
			: orDash(null);

		return code + STORE_LINE_SEPARATOR + name;
	}

	private loadStoreNames(): void {
		const codes = this.codes;

		if (this.tradingId === null || codes.length === 0) {
			this.storeNames = {};

			return;
		}

		this.cancelNamesRequest();

		const source = createCancelSource();
		this.namesSource = source;

		const pageSize = codes.length > STORE_LOOKUP_PAGE_CAP ? STORE_LOOKUP_PAGE_CAP : codes.length;
		const pager = createPaginationState(pageSize > 0 ? pageSize : 20);

		fetchAssignments(
			this.tradingId,
			{ competitorCodes: codes.slice() },
			buildPageQuery(pager),
			{ cancelToken: source.token },
		)
			.then((page) => {
				if (this.namesSource !== source) {
					return;
				}

				const next: { [code: string]: string } = {};
				const content = page && Array.isArray(page.content) ? page.content : [];

				for (let i = 0; i < content.length; i += 1) {
					const row = content[i];
					const store = row && row.store ? row.store : null;
					const code = store && store.competitorCode
						? String(store.competitorCode)
						: (row && row.competitorCode ? String(row.competitorCode) : '');

					if (code === '') {
						continue;
					}

					const name = store && store.name ? String(store.name).trim() : '';
					next[code] = name;
				}

				this.storeNames = next;
			})
			.catch((error) => {
				if (isCanceled(error) || this.namesSource !== source) {
					return;
				}

				this.storeNames = {};
			})
			.finally(() => {
				if (this.namesSource === source) {
					this.namesSource = null;
				}
			});
	}

	private cancelNamesRequest(): void {
		if (this.namesSource !== null) {
			this.namesSource.cancel();
			this.namesSource = null;
		}
	}
}
